'use client'

import { ChainType } from '@polaris/core/models'
import { useAtomValue } from 'jotai'
import { type PropsWithChildren, useMemo } from 'react'
import {
  fromTokenAcrossChainsAtom,
  routeErrorsAtom,
  swapRequirementsAtom,
  toTokenAcrossChainsAtom,
} from '@/src/stores/swap/atoms'
import { HYPERCORE_CHAIN_ID, HYPEREVM_CHAIN_ID } from '@/src/utils/consts'
import { ErrorInline } from '../ui/error-inline'

interface AmountInErrorsProps {
  className?: string
}

export function AmountInErrors({
  className,
  children,
}: PropsWithChildren<AmountInErrorsProps>) {
  const routeErrors = useAtomValue(routeErrorsAtom)
  const { fromChainNameNotSupportedByWallet, insufficientBalance } =
    useAtomValue(swapRequirementsAtom)
  const { data: fromToken, error: fromTokenError } = useAtomValue(
    fromTokenAcrossChainsAtom,
  )
  const { data: toToken } = useAtomValue(toTokenAcrossChainsAtom)

  const isBitcoinNetwork =
    fromToken?.requested_asset.chain_type === ChainType.Bitcoin

  const isNotFoundError = useMemo(() => {
    const cause = fromTokenError?.cause

    if (!cause || typeof cause !== 'string') return false

    return cause.includes('not found on chain')
  }, [fromTokenError?.cause])

  const error = useMemo(() => {
    if (isBitcoinNetwork) {
      // Context https://linear.app/osmosis/issue/POLFE-1121/remove-bitcoin-connector
      return 'Network not currently supported'
    }

    if (routeErrors?.invalidDestinationAddress) {
      return routeErrors.invalidDestinationAddress
    }

    if (
      routeErrors?.addressTypeInvalid &&
      fromToken?.requested_asset.chain_id !== HYPERCORE_CHAIN_ID &&
      fromToken?.requested_asset.chain_id !== HYPEREVM_CHAIN_ID
    ) {
      return 'Invalid address type'
    }

    if (isNotFoundError) {
      return 'Unknown token'
    }

    if (routeErrors?.noRouteIncreaseInputAmount) {
      return 'Try a higher amount'
    }

    if (
      routeErrors?.minAmount &&
      (fromToken?.requested_asset.chain_id === HYPERCORE_CHAIN_ID ||
        toToken?.requested_asset.chain_id === HYPERCORE_CHAIN_ID ||
        fromToken?.requested_asset.chain_id === HYPEREVM_CHAIN_ID ||
        toToken?.requested_asset.chain_id === HYPEREVM_CHAIN_ID)
    ) {
      return routeErrors.minAmount.message
    }

    if (insufficientBalance) {
      return 'Insufficient funds'
    }

    if (fromChainNameNotSupportedByWallet) {
      return `Network unsupported by ${fromChainNameNotSupportedByWallet.walletName}`
    }

    return undefined
  }, [
    routeErrors,
    fromToken?.requested_asset.chain_id,
    toToken?.requested_asset.chain_id,
    insufficientBalance,
    fromChainNameNotSupportedByWallet,
    isNotFoundError,
    isBitcoinNetwork,
  ])

  return (
    <ErrorInline
      className={className}
      variant={
        routeErrors?.noRouteIncreaseInputAmount ||
        fromChainNameNotSupportedByWallet ||
        isNotFoundError ||
        routeErrors?.noRouteIncreaseInputAmount ||
        isBitcoinNetwork
          ? 'primary'
          : 'danger'
      }
    >
      {error ?? children}
    </ErrorInline>
  )
}
